import { useState } from "react";
import { hotData, weekHotData } from "@/mocks/home";
import Reveal from "./Reveal";

type TabKey = "today" | "week";

interface Row {
  title: string;
  tag: string;
  heat: number;
  delta: number;
  time: string;
  hot: string;
}

const tagColor: Record<string, string> = {
  "娱乐": "bg-primary-100 text-primary-700",
  "综艺": "bg-accent-100 text-accent-800",
  "消费": "bg-secondary-100 text-secondary-800",
  "影视": "bg-primary-100 text-primary-700",
  "生活": "bg-accent-100 text-accent-800",
  "音乐": "bg-secondary-100 text-secondary-800",
  "行业": "bg-accent-100 text-accent-800",
  "观察": "bg-secondary-100 text-secondary-800",
  "总榜": "bg-primary-500 text-background-50",
};

export default function HotList() {
  const [tab, setTab] = useState<TabKey>("today");
  const data: Row[] = tab === "today" ? hotData : weekHotData;
  const maxHeat = Math.max(...data.map((d) => d.heat));

  return (
    <section id="hot" className="section-anchor bg-background-100/70 py-14 md:py-20">
      <div className="container-gua">
        <Reveal>
          <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-600">
                <i className="ri-arrow-right-up-line"></i>
                Live Ranking
              </span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                今日热榜
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                滚动热度由讨论声量与传播速度综合计算，陪你一眼看懂此刻大家都在聊什么。
              </p>
            </div>
            <div className="flex w-fit items-center rounded-full bg-background-50 p-1">
              {[
                { key: "today" as TabKey, label: "24h 热门" },
                { key: "week" as TabKey, label: "本周热议" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition ${
                    tab === t.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 gap-5">
          {data.map((row, idx) => (
            <Reveal key={row.title} delay={idx * 30}>
              <article className="group flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4 transition hover:border-primary-200 hover:bg-background-50 md:p-5">
                <div
                  className={`w-10 shrink-0 text-center font-heading text-2xl font-black md:w-12 md:text-3xl ${
                    idx === 0 ? "text-primary-500" : "text-background-400 group-hover:text-primary-400"
                  }`}
                >
                  {String(idx + 1).padStart(2, "0")}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold ${
                        tagColor[row.tag] || "bg-secondary-100 text-secondary-800"
                      }`}
                    >
                      {row.tag}
                    </span>
                    <span className="rounded-md bg-primary-500 px-2 py-0.5 text-xs font-bold text-background-50">
                      {row.hot}
                    </span>
                  </div>
                  <h3 className="mt-2 truncate text-sm font-semibold text-foreground-900 md:text-base">
                    {row.title}
                  </h3>
                  <div className="mt-2.5 hidden h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-background-100 sm:block">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-400"
                      style={{ width: `${(row.heat / maxHeat) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex shrink-0 flex-col items-end gap-1 text-right">
                  <span className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                    {row.heat} <span className="text-xs font-medium text-foreground-500">万热度</span>
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 text-xs ${
                      row.delta >= 0 ? "text-primary-600" : "text-secondary-600"
                    }`}
                  >
                    <i className={row.delta >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"}></i>
                    {row.delta >= 0 ? "+" : ""}
                    {row.delta}
                    <span className="text-foreground-400">· {row.time}</span>
                  </span>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}